/**
 * The install source sent once with guest auth (`GuestAuthRequest.installReferrer`), always in the
 * form Google Play records, `utm_source=…&utm_medium=…&gclid=…`, so the server parses one shape:
 *
 *   Android  Play's own referrer, read at first boot (native/deferred.ts).
 *   Web      the landing URL's campaign parameters, read at the first visit (below).
 *   iOS, Discord  none: the App Store carries nothing.
 *
 * It is stored in PlayerState.installReferrer when the player has no account yet and cleared once
 * guest creation succeeds (ApiClient.guest), so a failed first auth does not lose it and no later
 * call re-sends it. Nothing here is a player's own data: only the campaign parameters of the link
 * that brought them, never the rest of the query (a `?ref=` or a `?token=` is not ours to forward).
 */

/** The longest value sent (GuestAuthRequest.installReferrer). */
export const INSTALL_REFERRER_MAX = 1024;

/** The campaign parameters kept from a landing URL, in the order they are written. */
export const REFERRER_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid'] as const;

/**
 * `k=v&k=v` of the campaign parameters in a page's query string, or null when it has none. Values are
 * percent-encoded (spaces as %20, as Play writes them), empty ones dropped, and a pair that would
 * push the string past the cap is left out whole rather than cut mid-escape.
 */
export function webInstallReferrer(search: string): string | null {
  const q = new URLSearchParams(search);
  let out = '';
  for (const k of REFERRER_PARAMS) {
    const v = q.get(k)?.trim();
    if (!v) continue;
    const pair = `${k}=${encodeURIComponent(v)}`;
    const next = out ? `${out}&${pair}` : pair;
    if (next.length <= INSTALL_REFERRER_MAX) out = next;
  }
  return out || null;
}

/** The `utm_campaign` of a page, if it has one: the web banner passes it on to the Play listing. */
export function pageCampaign(search: string): string | null {
  return new URLSearchParams(search).get('utm_campaign')?.trim() || null;
}
